import { Injectable, NgZone, OnDestroy } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { timeout } from 'rxjs/operators'

import { environment } from '../../../../environments/environment'

const PING_TIMEOUT = 3000
const HEARTBEAT_INTERVAL = 5000

@Injectable({
  providedIn: 'root'
})
export class ConnectivityService implements OnDestroy {
  isOnline$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(navigator.onLine)

  private heartbeatInterval: any
  private onlineHandler: () => void
  private offlineHandler: () => void
  private initialCheckDone = false

  get isOnline(): boolean {
    return this.isOnline$.value
  }

  constructor(private http: HttpClient, private ngZone: NgZone) {
    this.onlineHandler = () => {
      // Don't trust the browser event blindly in WebView — verify with a real ping
      if (environment.nativescriptEmbedded) {
        this.verifyConnection()
      } else {
        this.updateStatus(true)
      }
    }

    this.offlineHandler = () => {
      this.updateStatus(false)
    }

    window.addEventListener('online', this.onlineHandler)
    window.addEventListener('offline', this.offlineHandler)

    if (environment.nativescriptEmbedded) {
      // Immediate connectivity check on startup — don't wait 30s
      this.verifyConnection().then(() => {
        this.initialCheckDone = true
      })
      this.startHeartbeat()
    }
  }

  ngOnDestroy() {
    window.removeEventListener('online', this.onlineHandler)
    window.removeEventListener('offline', this.offlineHandler)

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }
  }

  verifyConnection(): Promise<boolean> {
    const url = environment.apiBaseUrl + 'auth/me'
    return this.http
      .head(url, { observe: 'response', responseType: 'text' })
      .pipe(timeout(PING_TIMEOUT))
      .toPromise()
      .then(() => {
        this.updateStatus(true)
        return true
      })
      .catch((err) => {
        // An HTTP status (e.g. 401) means the server responded — we are online.
        // Only network errors (status 0) or timeouts mean we are offline.
        if (err && err.status && err.status > 0) {
          this.updateStatus(true)
          return true
        }
        this.updateStatus(false)
        return false
      })
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.verifyConnection()
    }, HEARTBEAT_INTERVAL)
  }

  private updateStatus(online: boolean): void {
    if (this.isOnline$.value !== online) {
      this.ngZone.run(() => {
        this.isOnline$.next(online)
      })
    }
  }
}
